import { 
  collection, 
  addDoc, 
  updateDoc,
  doc, 
  query,
  where,
  orderBy,
  limit,
  writeBatch,
  onSnapshot,
  Timestamp,
  getDocs,
  getDoc,
  setDoc
} from 'firebase/firestore';
import { db, auth } from './firebase';
import type { NotificationType, NotificationPreferences, NotificationInput } from '../types/notifications';

const DEFAULT_PREFERENCES = {
  starterFeeding: true,
  sessionReminders: true,
  challengeUpdates: true,
  systemNotifications: true,
  pushNotifications: true,
  emailNotifications: false,
  soundEnabled: true,
  desktopNotifications: true,
  quietHoursEnabled: false
};

export interface Notification {
  id: string;
  userId: string;
  type: NotificationType;
  title: string;
  message: string;
  icon?: string;
  read: boolean;
  createdAt: Date;
  expiresAt?: Date;
  processed?: boolean;
  pushSent?: boolean;
  data?: {
    starterId?: string;
    sessionId?: string;
    challengeId?: string;
    route?: string;
    [key: string]: any;
  };
}

// Helper function to convert Firestore data to Notification type
function convertNotification(doc: any): Notification {
  const data = doc.data();
  return {
    id: doc.id,
    userId: data.userId,
    type: data.type || 'system',
    title: data.title,
    message: data.message || data.body,
    read: data.read || false,
    processed: data.processed || false,
    pushSent: data.pushSent || false,
    createdAt: data.createdAt?.toDate() || new Date(),
    expiresAt: data.expiresAt?.toDate(),
    data: data.data || {}
  };
}

// Helper function to process and deduplicate notifications
function processNotifications(docs: any[]): Notification[] {
  // Use Map for efficient deduplication
  const notificationMap = new Map<string, Notification>();

  docs.forEach(doc => {
    const notification = convertNotification(doc);

    // Skip processed notifications
    if (notification.processed) {
      return;
    }

    // For starter notifications, use starterId as key to prevent duplicates
    // For session notifications, use sessionId and milestoneType
    const key = notification.data?.starterId
      ? `starter:${notification.data.starterId}`
      : notification.data?.sessionId && notification.data?.milestoneType
        ? `session:${notification.data.sessionId}:${notification.data.milestoneType}`
        : notification.id;

    // Only keep the most recent notification for each key
    const existing = notificationMap.get(key);
    if (!existing || notification.createdAt > existing.createdAt) {
      notificationMap.set(key, notification);
    }
  });

  const notifications = Array.from(notificationMap.values())
    .filter(n => {
      // Remove expired notifications
      if (n.expiresAt && n.expiresAt < new Date()) {
        updateDoc(doc(db, 'notifications', n.id), { processed: true })
          .catch(console.error);
        return false;
      }
      return true;
    })
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

  return notifications;
}

// Get notifications once (for initial load)
export async function getNotifications(): Promise<Notification[]> {
  const user = auth.currentUser;
  if (!user) {
    return [];
  }

  try {
    console.log('Fetching notifications for user:', user.uid);
    const notificationsRef = collection(db, 'notifications');
    const q = query(
      notificationsRef,
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const snapshot = await getDocs(q);
    console.log('Fetched notification docs:', snapshot.docs.length);

    const notifications = processNotifications(snapshot.docs);
    console.log('Processed notifications:', notifications.length);

    return notifications;
  } catch (error) {
    console.error('Error fetching notifications:', error);
    return [];
  }
}

// Notification subscription with deduplication
export function subscribeToNotifications(callback: (notifications: Notification[]) => void) {
  const user = auth.currentUser;
  if (!user) {
    callback([]);
    return () => {};
  }

  try {
    console.log('Setting up notification subscription for user:', user.uid);
    const notificationsRef = collection(db, 'notifications');
    const q = query(
      notificationsRef,
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(q, {
      next: (snapshot) => {
        console.log('Received notification snapshot, docs:', snapshot.docs.length);
        const notifications = processNotifications(snapshot.docs);
        console.log('Filtered notifications:', notifications.length);
        callback(notifications);
      },
      error: (error) => {
        console.error('Error in notification subscription:', error);
        // Attempt to resubscribe after delay if still authenticated
        setTimeout(() => {
          if (user.uid === auth.currentUser?.uid) {
            subscribeToNotifications(callback);
          }
        }, 5000);
      }
    });

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up notification subscription:', error);
    return () => {};
  }
}

export async function addNotification(
  userId: string,
  notification: NotificationInput,
  isLoginCheck: boolean = false
): Promise<string | null> {
  try {
    console.log('Adding notification:', notification, 'isLoginCheck:', isLoginCheck);
    const notificationsRef = collection(db, 'notifications');

    // For session notifications, check if we already have a notification for this milestone
    if (notification.type.startsWith('session_') && notification.data?.sessionId && notification.data?.milestoneType) {
      const existingQuery = query(
        notificationsRef,
        where('userId', '==', userId),
        where('processed', '==', false),
        where('type', '==', notification.type),
        where('data.sessionId', '==', notification.data.sessionId),
        where('data.milestoneType', '==', notification.data.milestoneType)
      );

      const existingDocs = await getDocs(existingQuery);
      if (!existingDocs.empty) {
        console.log('Found existing session milestone notification:',
          notification.data.sessionId, notification.data.milestoneType);
        return existingDocs.docs[0].id;
      }
    }
    // For starter notifications, check if we already have a notification for this starter
    else if (notification.type === 'starter_feeding' && notification.data?.starterId) {
      const existingQuery = query(
        notificationsRef,
        where('userId', '==', userId),
        where('processed', '==', false),
        where('type', '==', notification.type),
        where('data.starterId', '==', notification.data.starterId)
      );

      const existingDocs = await getDocs(existingQuery);
      if (!existingDocs.empty) {
        const existingDoc = existingDocs.docs[0];
        const existingData = existingDoc.data();
        const existingCreatedAt = existingData.createdAt?.toDate();

        // On login check, be more aggressive about refreshing notifications
        const timeThreshold = isLoginCheck
          ? new Date(Date.now() - 15 * 60 * 1000) // 15 minutes for login checks
          : new Date(Date.now() - 2 * 60 * 60 * 1000); // 2 hours for regular checks

        // Check if the existing notification is older than threshold
        if (existingCreatedAt) {
          if (existingCreatedAt < timeThreshold) {
            console.log(`Existing starter notification is old (>${isLoginCheck ? '15 min' : '2 hours'}), marking as processed and creating new one`);
            // Mark old notification as processed
            await updateDoc(doc(db, 'notifications', existingDoc.id), {
              processed: true,
              pushSent: true
            });
            // Continue to create new notification below
          } else {
            console.log('Found recent starter notification:', notification.data.starterId, 'age:', Math.round((Date.now() - existingCreatedAt.getTime()) / 60000), 'minutes');
            return existingDoc.id;
          }
        } else {
          console.log('Found existing starter notification with no timestamp:', notification.data.starterId);
          return existingDoc.id;
        }
      }
    }

    // Ensure all required fields are present and properly formatted
    const notificationData = {
      userId,
      type: notification.type,
      title: notification.title,
      message: notification.message,
      data: notification.data || {},
      read: false,
      processed: false,
      pushSent: false,
      createdAt: Timestamp.now()
    };

    // Only add expiresAt if it's provided and valid
    if (notification.expiresAt && notification.expiresAt instanceof Date) {
      notificationData.expiresAt = Timestamp.fromDate(notification.expiresAt);
    }

    console.log('Notification data to save:', notificationData);
    const docRef = await addDoc(notificationsRef, notificationData);
    console.log('Notification added with ID:', docRef.id);

    return docRef.id;
  } catch (error) {
    console.error('Error adding notification:', error);
    return null;
  }
}

export async function markNotificationAsRead(notificationId: string) {
  try {
    const notificationRef = doc(db, 'notifications', notificationId);
    await updateDoc(notificationRef, { read: true });
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
}

export async function markAllNotificationsAsRead() {
  const user = auth.currentUser;
  if (!user) throw new Error('User must be authenticated');

  try {
    const notificationsRef = collection(db, 'notifications');
    const q = query(
      notificationsRef,
      where('userId', '==', user.uid),
      where('read', '==', false),
      where('processed', '==', false)
    );
    
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return;
    }

    const batch = writeBatch(db);
    
    snapshot.docs.forEach((doc) => {
      batch.update(doc.ref, { read: true });
    });

    await batch.commit();
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw error;
  }
}

export async function deleteNotification(notificationId: string) {
  try {
    const notificationRef = doc(db, 'notifications', notificationId);
    await updateDoc(notificationRef, { 
      processed: true,
      read: true 
    });
  } catch (error) {
    console.error('Error deleting notification:', error);
    throw error;
  }
}

export async function deleteAllNotifications() {
  const user = auth.currentUser;
  if (!user) throw new Error('User must be authenticated');

  try {
    const notificationsRef = collection(db, 'notifications');
    const q = query(
      notificationsRef,
      where('userId', '==', user.uid),
      where('processed', '==', false)
    );
    
    const snapshot = await getDocs(q);
    const batch = writeBatch(db);
    
    snapshot.docs.forEach((doc) => {
      batch.update(doc.ref, { 
        processed: true,
        read: true 
      });
    });

    await batch.commit();
  } catch (error) {
    console.error('Error deleting all notifications:', error);
    throw error;
  }
}

export async function getUserNotificationPreferences(userId: string): Promise<NotificationPreferences> {
  try {
    const prefsRef = doc(db, 'users', userId, 'settings', 'notifications');
    const snapshot = await getDoc(prefsRef);

    if (!snapshot.exists()) {
      // Initialize default preferences
      const defaultPrefs = {
        ...DEFAULT_PREFERENCES,
        userId
      };
      await setDoc(prefsRef, defaultPrefs);
      return defaultPrefs;
    }

    return snapshot.data() as NotificationPreferences;
  } catch (error) {
    console.error('Error getting notification preferences:', error);
    // Return default preferences if there's an error
    return { ...DEFAULT_PREFERENCES, userId };
  }
}

export async function updateNotificationPreferences(
  preferences: Partial<NotificationPreferences>
): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error('User must be authenticated');

  try {
    const prefsRef = doc(db, 'users', user.uid, 'settings', 'notifications');
    await setDoc(prefsRef, preferences, { merge: true });
  } catch (error) {
    console.error('Error updating notification preferences:', error);
    throw error;
  }
}

export async function sendTestNotification() {
  const user = auth.currentUser;
  if (!user) throw new Error('User must be authenticated');

  try {
    const testNotification = {
      type: 'system' as const,
      title: 'Test Notification',
      message: 'This is a test notification to verify the system is working.',
      data: {
        route: '/',
        test: true
      }
    };

    return await addNotification(user.uid, testNotification);
  } catch (error) {
    console.error('Error sending test notification:', error);
    throw error;
  }
}